import React, { useEffect, useState } from "react";
import { Typography, Spin, message } from "antd";
import { IconCoins, IconTrendingUp, IconTrendingDown, IconGift, IconRefresh, IconShoppingBag } from "@tabler/icons-react";
import GlobalHeader from "../util/GlobalHeader";
import CreditServ from "../service/CreditServ";
import { formatDateTime } from "../util/dateUtils";
import { usePreference } from "../context/PreferenceContext";
import DataTable from "../component/ui/DataTable";
import "../css/myCredits.css";
import EmptyState from "../util/EmptyState";

const { Text } = Typography;

const MyCredits = () => {
    const [loading, setLoading] = useState(false);
    const [balance, setBalance] = useState(0);
    const { t, formatPrice } = usePreference();
    const [transactions, setTransactions] = useState([]);
    const [stats, setStats] = useState({});
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchCreditData = async (showLoading = true) => {
        try {
            if (showLoading && transactions.length === 0) setLoading(true);
            const cachedBalance = localStorage.getItem('cached_buyer_credit_balance');
            const cachedStats = localStorage.getItem('cached_buyer_credit_stats');
            const cachedTrans = localStorage.getItem('cached_buyer_credit_trans');
            
            if (cachedBalance && cachedStats && cachedTrans && showLoading) {
                try {
                    setBalance(Number(cachedBalance));
                    setStats(JSON.parse(cachedStats));
                    setTransactions(JSON.parse(cachedTrans));
                    setLoading(false); // Stop skeleton immediately
                } catch(e) {}
            }

            // Fetch balance and stats
            const [balanceRes, statsRes, transactionsRes] = await Promise.all([
                CreditServ.getBalance(),
                CreditServ.getStats(),
                CreditServ.getTransactions({ limit: 50 })
            ]);

            const newBalance = balanceRes?.data?.balance || 0;
            const newStats = statsRes?.data || {};
            const newTrans = transactionsRes?.data || [];

            localStorage.setItem('cached_buyer_credit_balance', newBalance.toString());
            localStorage.setItem('cached_buyer_credit_stats', JSON.stringify(newStats));
            localStorage.setItem('cached_buyer_credit_trans', JSON.stringify(newTrans));

            setBalance(newBalance);
            setStats(newStats);
            setTransactions(newTrans);
        } catch (error) {
            console.error("Error fetching credit data:", error);
            message.error("Failed to load credit information");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCreditData(true);
    }, []);

    const getTypeIcon = (type) => {
        const icons = {
            earned: <IconTrendingUp size={16} color="#52c41a" />,
            spent: <IconTrendingDown size={16} color="#ff4d4f" />,
            refund: <IconRefresh size={16} color="#1890ff" />,
            bonus: <IconGift size={16} color="#722ed1" />,
            referral: <IconGift size={16} color="#fa8c16" />
        };
        return icons[type] || <IconCoins size={16} />;
    };



    const columns = [
        {
            title: t('date'),
            dataIndex: "createdAt",
            key: "createdAt",
            render: (date) => <Text type="secondary" style={{ fontSize: 13 }}>{formatDateTime(date)}</Text>,
            width: 180
        },
        {
            title: t('type'),
            dataIndex: "type",
            key: "type",
            render: (type) => {
                const colors = {
                    earned: { bg: '#dcfce7', color: '#15803d' },
                    spent: { bg: '#fee2e2', color: '#dc2626' },
                    refund: { bg: '#dbeafe', color: '#1d4ed8' },
                    bonus: { bg: '#f3e8ff', color: '#7e22ce' },
                    referral: { bg: '#fef3c7', color: '#b45309' }
                };
                const style = colors[type] || { bg: '#f1f5f9', color: '#475569' };
                return (
                    <span style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "4px 10px",
                        borderRadius: "8px",
                        fontSize: "12px",
                        fontWeight: 600,
                        background: style.bg,
                        color: style.color
                    }}>
                        {getTypeIcon(type)}
                        {type.toUpperCase()}
                    </span>
                );
            },
            width: 150
        },
        {
            title: t('description'),
            dataIndex: "description",
            key: "description",
            render: (desc) => <Text strong style={{ fontSize: 13, color: '#334155' }}>{desc}</Text>,
            ellipsis: true
        },
        {
            title: t('amount'),
            dataIndex: "amount",
            key: "amount",
            render: (amount) => (
                <Text strong style={{ fontSize: 14 }} className={amount >= 0 ? "pcu-color-success" : "pcu-color-error"}>
                    {amount >= 0 ? "+" : ""}{formatPrice(Math.abs(amount))}
                </Text>
            ),
            width: 120,
            align: "right"
        },
        {
            title: t('credit_balance'),
            dataIndex: "balance",
            key: "balance",
            render: (balance) => <Text strong style={{ fontSize: 13, color: '#64748b' }}>{formatPrice(balance)}</Text>,
            width: 150,
            align: "right"
        }
    ];

    const statsData = stats?.stats || {};

    return (
        <div className="credits-page-container">
            <div className="credits-content">
                <GlobalHeader title={
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontWeight: 800 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #fef9c3, #fef08a)', border: '1px solid rgba(234,179,8,0.3)' }}>
                            <IconCoins size={20} color="#ca8a04" stroke={1.8} />
                        </span>
                        {t('my_credits')}
                    </span>
                }>

                    {loading ? (
                        <div className="pcu-text-center pcu-padding-y-100">
                            <Spin size="large" />
                        </div>
                    ) : (
                        <>
                            {/* Balance Card Section */}
                            <div className="unique-balance-card">
                                <div className="unique-balance-glow"></div>
                                <div className="unique-balance-content">
                                    <div>
                                        <span className="unique-balance-label">{t('available_credits_label')}</span>
                                        <h1 className="unique-balance-amount">{formatPrice(balance)}</h1>
                                    </div>
                                    <div className="unique-coin-icon-wrapper">
                                        <IconCoins size={48} color="#e0e7ff" />
                                    </div>
                                </div>
                            </div>

                            {/* Statistics Grid */}
                            <div className="unique-stats-grid">
                                <div className="unique-stat-card stat-spent">
                                    <span className="unique-stat-title">{t('total_spent')}</span>
                                    <p className="unique-stat-value">
                                        {formatPrice(stats.totalSpent || 0)}
                                        <span className="unique-stat-suffix">({stats.totalSpentCount || 0} {t('times_count')})</span>
                                    </p>
                                </div>
                                <div className="unique-stat-card stat-bonus">
                                    <span className="unique-stat-title">{t('bonus_credits')}</span>
                                    <p className="unique-stat-value">
                                        {formatPrice(statsData.bonus?.total || 0)}
                                        <span className="unique-stat-suffix">({statsData.bonus?.count || 0} {t('times_count')})</span>
                                    </p>
                                </div>
                                <div className="unique-stat-card stat-refund">
                                    <span className="unique-stat-title">{t('refunds')}</span>
                                    <p className="unique-stat-value">
                                        {formatPrice(stats.totalRefunds || 0)}
                                        <span className="unique-stat-suffix">({stats.totalRefundsCount || 0} {t('times_count')})</span>
                                    </p>
                                </div>
                            </div>

                            {/* Transaction History */}
                            <div className="unique-history-card">
                                <div className="unique-history-header">
                                    <IconShoppingBag size={20} color="#475569" />
                                    <h3 className="unique-history-title">{t('transaction_history')}</h3>
                                </div>
                                {transactions.length === 0 ? (
                                    <div className="pcu-padding-40">
                                        <EmptyState
                                            title="No Transactions Logged"
                                            description={t('no_transactions_yet') || "You haven't made any transactions with your credits yet."}
                                            gradient="green"
                                            showAction={false}
                                            icon="inbox"
                                        />
                                    </div>
                                ) : (
                                    isMobile ? (
                                        <div className="pcu-padding-16">
                                            {transactions.map(tx => (
                                                <div
                                                    key={tx._id}
                                                    className="pcu-margin-bottom-12 pcu-radius-12"
                                                    style={{ border: '1px solid #e2e8f0', padding: 16 }}
                                                >
                                                    <div className="pcu-flex pcu-justify-between pcu-margin-bottom-12 pcu-align-center">
                                                        <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 8px', borderRadius: 6, background: '#f1f5f9', color: '#475569', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                            {getTypeIcon(tx.type)} {tx.type.toUpperCase()}
                                                        </span>
                                                        <Text type="secondary" className="pcu-font-12">{new Date(tx.createdAt).toLocaleDateString()}</Text>
                                                    </div>
                                                    <div className="pcu-margin-bottom-12">
                                                        <Text className="pcu-font-14" style={{ color: '#1e293b' }} strong>{tx.description}</Text>
                                                    </div>
                                                    <div className="pcu-flex pcu-justify-between pcu-items-center pcu-padding-top-12" style={{ borderTop: '1px dashed #e2e8f0' }}>
                                                        <Text type="secondary" style={{ fontSize: 13 }}>{t('credit_balance')}: {formatPrice(tx.balance)}</Text>
                                                        <Text strong className={tx.amount >= 0 ? "pcu-color-success pcu-font-16" : "pcu-color-error pcu-font-16"}>
                                                            {tx.amount >= 0 ? "+" : ""}{formatPrice(Math.abs(tx.amount))}
                                                        </Text>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <DataTable
                                            columns={columns}
                                            data={transactions}
                                            pagination={{ pageSize: 10 }}
                                        />
                                    )
                                )}
                            </div>
                        </>
                    )}
                </GlobalHeader>
            </div>
        </div>
    );
};

export default MyCredits;

